import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit, Loader2, Calendar, User, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ManageBlogs = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);

    // Form State
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [content, setContent] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
    const [image, setImage] = useState(null);

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewblogs`, {
                headers: { authorization: `Bearer ${token}` }
            }); 
            setBlogs(res.data.blogs);
        } catch (err) {
            console.error("Error fetching blogs:", err);
        } finally {
            setFetchLoading(false);
        } 
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('author', author);
        formData.append('excerpt', excerpt);
        formData.append('content', content);
        formData.append('date', date);
        formData.append('image', image);

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/insertblog`, formData, {
                headers: { 
                    'Content-Type': 'multipart/form-data', 
                    authorization: `Bearer ${token}` 
                }
            });
            alert("Blog Posted successfully!");
            // Reset Form
            setTitle(''); setAuthor(''); setExcerpt(''); setContent(''); setImage(null);
            fetchBlogs();
        } catch (err) {
            alert("Failed to post blog: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteBlog = async (id) => {
        if (!window.confirm("Are you sure you want to delete this blog?")) return;
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deleteblog/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setBlogs(blogs.filter(b => b._id !== id));
        } catch (err) {
            alert("Delete failed");
        }
    };

    if (fetchLoading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-10">
            <h1 className="text-3xl font-black uppercase italic">Editorial <span className="text-orange-500">Center</span></h1>

            {/* ADD BLOG FORM */}
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Blog Title</label>
                        <input type="text" value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" required />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Author Name</label>
                        <input type="text" value={author} onChange={(e)=>setAuthor(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" required />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Publish Date</label>
                        <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" required />
                    </div>
                </div>

                <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-slate-400">Short Excerpt (Summary)</label>
                    <input type="text" value={excerpt} onChange={(e)=>setExcerpt(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl" placeholder="A brief hook for the readers..." required />
                </div>

                <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-slate-400">Main Content</label>
                    <textarea value={content} onChange={(e)=>setContent(e.target.value)} className="w-full p-4 bg-slate-50 rounded-xl h-40" placeholder="Write your story here..." required />
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <label className="text-[10px] font-black uppercase text-slate-400">Cover Image</label>
                    <input type="file" onChange={(e)=>setImage(e.target.files[0])} className="w-full p-3 bg-slate-50 rounded-2xl file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-700 cursor-pointer" required />
                </div>

                <button type="submit" disabled={loading} className="w-full py-4 bg-orange-500 text-white font-black uppercase rounded-2xl hover:bg-slate-900 transition-all flex justify-center items-center gap-2">
                    {loading ? <Loader2 className="animate-spin" /> : "Publish Blog Post"}
                </button>
            </form>

            {/* BLOGS LIST TABLE */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase">
                        <tr>
                            <th className="p-5">Visual</th>
                            <th className="p-5">Title & Author</th>
                            <th className="p-5">Date</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {blogs.map((b) => (
                            <tr key={b._id} className="hover:bg-slate-50/50">
                                <td className="p-5">
                                    <img src={b.image} className="w-16 h-10 object-cover rounded-lg border shadow-sm" alt="blog" />
                                </td>
                                <td className="p-5"> 
                                    <p className="font-black uppercase text-slate-900 line-clamp-1">{b.title}</p>
                                    <p className="text-[10px] text-orange-500 font-bold uppercase">{b.author}</p>
                                </td>
                                <td className="p-5">
                                    <p className="text-xs text-slate-500 font-medium">
                                        {new Date(b.date).toLocaleDateString()}
                                    </p>
                                </td>
                                <td className="p-5">
                                    <div className="flex justify-center gap-2">
                                        <button 
                                            onClick={() => navigate(`/admin/update-blog/${b._id}`)}
                                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all">
                                            <Edit size={16}/>
                                        </button>
                                        <button 
                                            onClick={() => deleteBlog(b._id)}
                                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all">
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ManageBlogs;